import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/payment-integrations`;

/** InfinitePay: InfiniteTag do App (a API aceita até 100 caracteres) */
export const HANDLE_MAX = 100;

/**
 * Integrações de pagamento online da empresa
 * (PaymentIntegrationController - /payment-integrations, ADMIN/OWNER).
 * PaymentIntegrationDTO: { provider, providerName, implemented, configured,
 * enabled, handle, updatedAt, updatedBy, version }. `provider` é o slug da
 * rota (infinitepay, mercadopago...). Só uma integração fica ativa por
 * empresa: ativar uma desativa a outra no backend.
 * Erros: 409 = `version` desatualizada (alteração concorrente);
 * owner global sem empresa escolhida → TENANT001.
 */
class PaymentIntegrationService {
  /**
   * Todos os provedores conhecidos, com a configuração da empresa atual
   * (implemented=false = ainda sem adaptador na API).
   * @param {object} [options] - config extra do axios (ex.: { silent: true })
   */
  async getAll(options = {}) {
    try {
      const response = await api.get(API_BASE, options);
      return unwrapList(response);
    } catch (error) {
      console.error("❌ Erro ao buscar integrações de pagamento:", error);
      throw error;
    }
  }

  /** @param {string} provider - slug (ex.: "infinitepay") */
  async getByProvider(provider, options = {}) {
    try {
      const response = await api.get(`${API_BASE}/${encodeURIComponent(provider)}`, options);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar a integração ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Cria/atualiza a configuração (PUT /{provider}).
   * `enabled`: true salva e ativa; false salva desativada; omitido mantém.
   * Envie a `version` lida no GET (divergente → 409).
   * @param {string} provider
   * @param {{handle?: string, enabled?: boolean, version?: number|null}} data
   * @param {object} [options] - config extra do axios
   */
  async save(provider, data, options = {}) {
    try {
      const response = await api.put(`${API_BASE}/${encodeURIComponent(provider)}`, data, options);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao salvar a integração ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Ativa (desativando a que estava ativa) ou desativa o provedor.
   * Ativar exige a integração configurada (senão 400).
   * @param {string} provider
   * @param {boolean} enabled
   */
  async setEnabled(provider, enabled, options = {}) {
    try {
      const action = enabled ? "activate" : "deactivate";
      const response = await api.put(`${API_BASE}/${encodeURIComponent(provider)}/${action}`, null, options);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao ${enabled ? "ativar" : "desativar"} a integração ${provider}:`, error);
      throw error;
    }
  }

  /** InfiniteTag sem o "$" e sem espaços (a API também remove o "$") */
  normalizeHandle(value) {
    return String(value ?? "")
      .trim()
      .replace(/^\$+/, "")
      .trim();
  }

  /**
   * @param {{handle: string}} data
   * @returns {{errors: object, isValid: boolean}}
   */
  validateInfinitePay(data) {
    const errors = {};
    const handle = this.normalizeHandle(data?.handle);
    if (!handle) {
      errors.handle = "Informe a InfiniteTag da sua conta InfinitePay";
    } else if (handle.length > HANDLE_MAX) {
      errors.handle = `Use no máximo ${HANDLE_MAX} caracteres`;
    } else if (/\s/.test(handle)) {
      errors.handle = "A InfiniteTag não pode ter espaços";
    }
    return { errors, isValid: Object.keys(errors).length === 0 };
  }

  /** updatedAt (LocalDateTime: ISO ou array do Jackson) → "22/10/2026 14:30" */
  formatUpdatedAt(value) {
    if (!value) return "";
    let date;
    if (Array.isArray(value)) {
      const [y, m = 1, d = 1, h = 0, min = 0, s = 0] = value;
      date = new Date(y, m - 1, d, h, min, s);
    } else {
      date = new Date(value);
    }
    if (Number.isNaN(date.getTime())) return String(value);
    return `${date.toLocaleDateString("pt-BR")} ${date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  }
}

export default new PaymentIntegrationService();
